/** Éditeur / lecture devis. */
import { useEffect, useRef, useState } from "react";
import { Button } from "../../components/Button";
import { FxDate } from "../../components/FxDate";
import { FxSelect } from "../../components/FxSelect";
import { RichText } from "../../components/RichText";
import { useFormatters } from "../../formats/useFormatters";
import "../clients/clientShow.css";
import { clientSelectOption } from "../clients/format";
import type { ClientListItem } from "../clients/types";
import { typedDecimal, type TaxRate } from "../catalog/types";
import { QuoteField } from "./QuoteField";
import { openQuoteArchive, printQuotePdf, quotePrintUrl } from "./quotePdf";
import { QuotePdfDrawer } from "./QuotePdfDrawer";
import { QuoteTraceDrawer } from "./QuoteTraceDrawer";
import type { GatewayProvider } from "../../payments/gateways";
import { QuoteDocMenu } from "./QuoteDocMenu";
import { StatusChip } from "../../status/StatusChip";
import { draftTotals, docKindTitle } from "./format";
import { QuoteLines } from "./QuoteLines";
import type { DocKind, Quote, QuoteDraft } from "./types";
import "./quotes.css";

type QuoteEditorProps = {
  kind?: DocKind;
  companyId: number;
  draft: QuoteDraft;
  setDraft: (next: QuoteDraft) => void;
  clients: ClientListItem[];
  rates: TaxRate[];
  decimalComma: boolean;
  defaultTaxId: number | null;
  busy: boolean;
  error: string;
  notice?: string;
  onSave: () => void;
  onCancel: () => void;
  onSend?: () => void;
  onCloneQuote?: () => void;
  onCloneInvoice?: () => void;
  onCloneRecurring?: () => void;
  onPay?: () => void;
  linked?: ReadonlySet<GatewayProvider>;
  onCollect?: () => void;
  onGcRefresh?: () => void;
  onConvert?: () => void;
  onCredit?: () => void;
  onOpenCredit?: () => void;
  onDelete?: () => void;
  source?: Quote | null;
};

export function QuoteEditor({
  kind = "quote",
  companyId,
  draft,
  setDraft,
  clients,
  rates,
  decimalComma,
  defaultTaxId,
  busy,
  error,
  notice,
  onSave,
  onCancel,
  onSend,
  onCloneQuote,
  onCloneInvoice,
  onCloneRecurring,
  onPay,
  linked,
  onCollect,
  onGcRefresh,
  onConvert,
  onCredit,
  onOpenCredit,
  onDelete,
}: QuoteEditorProps) {
  const { money, day } = useFormatters();
  const [pdfOpen, setPdfOpen] = useState(false);
  const [traceOpen, setTraceOpen] = useState(false);
  const pdfFrame = useRef<HTMLIFrameElement>(null);
  const tot = draftTotals(draft.lines, rates, decimalComma, draft.discount, draft.discount_kind);
  const locked = draft.immutable;
  const previewUrl = draft.id ? quotePrintUrl(draft.id) : "";

  useEffect(() => {
    setPdfOpen(false);
    setTraceOpen(false);
  }, [draft.id]);

  function openPdf() {
    setPdfOpen(true);
  }

  function printPdf() {
    if (draft.id) printQuotePdf(draft.id, pdfFrame.current, draft.number, draft.archived === true, Boolean(draft.signature));
  }

  const isInv = kind === "invoice";
  const isCredit = kind === "credit_note";
  const title = docKindTitle(kind);
  const statusRow = {
    ...draft,
    doc_type: kind,
    viewed_at: draft.viewed_at,
    signed_at: draft.signature?.signed_at,
    paid: draft.paid,
    paid_cents: draft.paid_cents,
    credited: draft.credited,
    credit_note_id: draft.credit_note_id,
  };

  function set<K extends keyof QuoteDraft>(key: K, value: QuoteDraft[K]) {
    setDraft({ ...draft, [key]: value });
  }

  return (
    <div className="fx-quote-stage">
    <form
      className="fx-quote-form fx-quote-stage__edit"
      onSubmit={(e) => {
        e.preventDefault();
        if (!locked) onSave();
      }}
    >
      <header className="fx-quote-top">
        <div>
          <button type="button" className="fx-fiche-back" onClick={onCancel}>
            ← {docKindTitle(kind, true)}
          </button>
          <div className="fx-quote-top__title">
            <h1>
              {draft.id
                ? locked
                  ? `${title} ${draft.number || ""}`
                  : kind === "quote"
                    ? "Modifier le devis"
                    : kind === "invoice"
                      ? "Modifier la facture"
                      : "Modifier l’avoir"
                : kind === "quote"
                  ? "Nouveau devis"
                  : kind === "invoice"
                    ? "Nouvelle facture"
                    : "Nouvel avoir"}
            </h1>
            {draft.id ? <StatusChip row={statusRow} /> : null}
          </div>
        </div>
        <div className="fx-quote-top__acts">
          <Button variant="ghost" onClick={onCancel}>
            {locked ? "Retour" : "Annuler"}
          </Button>
          {draft.id && onSend ? (
            <QuoteDocMenu
              busy={busy}
              isDraft={!locked}
              converted={Boolean(draft.converted_invoice_id)}
              onCloneQuote={onCloneQuote}
              onCloneInvoice={onCloneInvoice}
              onCloneRecurring={onCloneRecurring}
              onPay={onPay}
              linked={linked}
              onCollect={onCollect}
              onGcRefresh={onGcRefresh}
              onSend={onSend}
              onPdf={openPdf}
              onPrint={printPdf}
              onArchive={locked && draft.id ? () => openQuoteArchive(draft.id) : undefined}
              onTrace={() => setTraceOpen(true)}
              onConvert={!isInv && !isCredit && locked && !draft.converted_invoice_id && !draft.refusal ? onConvert : undefined}
              onCredit={isInv && locked && !draft.credited ? onCredit : undefined}
              onOpenCredit={isInv && draft.credit_note_id ? onOpenCredit : undefined}
              onDelete={!locked ? onDelete : undefined}
            />
          ) : null}
          {!locked ? (
            <Button type="submit" disabled={busy}>
              Enregistrer
            </Button>
          ) : null}
        </div>
      </header>

      {locked ? (
        <p className="fx-quote-flash is-lock" role="status">
          {isCredit
            ? "Cet avoir est verrouillé et ne peut pas être modifié."
            : isInv
              ? "Cette facture est verrouillée et ne peut pas être modifiée."
              : "Ce devis est verrouillé et ne peut pas être modifié."}
        </p>
      ) : null}
      {isInv && draft.credited ? (
        <p className="fx-quote-flash is-lock" role="status">
          Facture annulée par un avoir. La pièce d’origine reste figée.
        </p>
      ) : null}
      {notice ? <p className="fx-quote-flash is-ok" role="status">{notice}</p> : null}
      {error ? <p className="fx-quote-flash is-err">{error}</p> : null}
      {!isInv && draft.refusal ? (
        <p className="fx-quote-flash is-lock" role="status">
          Refusé par {draft.refusal.refuser_name || "le client"} le {day(draft.refusal.refused_at)}
          {draft.refusal.reason ? ` — ${draft.refusal.reason}` : ""}. Le devis reste scellé.
        </p>
      ) : null}
      {!isInv && draft.signature ? (
        <p className="fx-quote-flash is-ok">
          Signé électroniquement par {draft.signature.signer_name} le {day(draft.signature.signed_at)}
          {draft.signature.proof_hash ? ` · empreinte ${draft.signature.proof_hash}` : ""}
        </p>
      ) : null}

      <section
        className={`fx-quote-meta${kind !== "quote" ? " is-bill" : ""}${isCredit && draft.related_number ? " is-credit" : ""}`}
        aria-label="En-tête"
      >
        <QuoteField label="Client" htmlFor="quote_client">
          <FxSelect
            id="quote_client"
            value={draft.client_id ? String(draft.client_id) : ""}
            disabled={locked}
            searchable
            placeholder="— Choisir —"
            options={clients.map(clientSelectOption)}
            onChange={(v) => set("client_id", Number(v) || 0)}
          />
        </QuoteField>
        {isCredit && draft.related_number ? (
          <QuoteField label="Facture">
            <input className="fx-q-ctrl" value={draft.related_number} disabled readOnly />
          </QuoteField>
        ) : null}
        <QuoteField label="Date d’émission" htmlFor="quote_issue">
          <FxDate
            id="quote_issue"
            value={draft.issue_date}
            disabled={locked}
            onChange={(v) => {
              const synced = !draft.operation_date || draft.operation_date === draft.issue_date;
              setDraft({ ...draft, issue_date: v, operation_date: synced ? v : draft.operation_date });
            }}
          />
        </QuoteField>
        {kind !== "quote" ? (
          <QuoteField label="Date d’opération" htmlFor="quote_op">
            <FxDate
              id="quote_op"
              value={draft.operation_date || draft.issue_date}
              disabled={locked}
              onChange={(v) => set("operation_date", v)}
            />
          </QuoteField>
        ) : null}
        <QuoteField label={kind === "quote" ? "Valable jusqu’au" : "Échéance"} htmlFor="quote_due">
          <FxDate id="quote_due" value={draft.due_date} disabled={locked} onChange={(v) => set("due_date", v)} />
        </QuoteField>
        <QuoteField label="Remise" htmlFor="quote_off">
          <div className="fx-q-unit">
            <input
              id="quote_off"
              className="fx-q-ctrl is-end"
              value={draft.discount}
              disabled={locked}
              inputMode="decimal"
              placeholder="0"
              onChange={(e) => set("discount", typedDecimal(e.target.value, decimalComma))}
            />
            <FxSelect
              plain
              value={draft.discount_kind}
              disabled={locked}
              options={[
                { value: "pct", label: "%" },
                { value: "eur", label: "€" },
              ]}
              onChange={(v) => set("discount_kind", v === "eur" ? "eur" : "pct")}
            />
          </div>
        </QuoteField>
        <QuoteField label="Note" htmlFor="quote_notes">
          <input
            id="quote_notes"
            className="fx-q-ctrl"
            name="notes"
            value={draft.notes}
            disabled={locked}
            placeholder="Mention au client…"
            onChange={(e) => set("notes", e.target.value)}
          />
        </QuoteField>
      </section>

      <QuoteLines
        companyId={companyId}
        lines={draft.lines}
        rates={rates}
        decimalComma={decimalComma}
        locked={locked}
        defaultTaxId={defaultTaxId}
        onChange={(lines) => set("lines", lines)}
      />

      <div className="fx-quote-recap">
        <div className="fx-quote-legal">
          <RichText
            label="Conditions"
            compact
            disabled={locked}
            value={draft.terms_html}
            resetKey={`${draft.id ?? "new"}-terms`}
            onChange={(html) => set("terms_html", html)}
          />
          <RichText
            label="Pied de page"
            compact
            disabled={locked}
            value={draft.footer_html}
            resetKey={`${draft.id ?? "new"}-footer`}
            onChange={(html) => set("footer_html", html)}
          />
        </div>
        <table className="fx-quote-tot">
          <tbody>
            <tr>
              <th>Sous-total HT</th>
              <td>{money(tot.sub)}</td>
            </tr>
            {tot.off > 0 ? (
              <tr>
                <th>Remise</th>
                <td>− {money(tot.off)}</td>
              </tr>
            ) : null}
            <tr>
              <th>Total HT</th>
              <td>{money(tot.ht)}</td>
            </tr>
            <tr>
              <th>TVA</th>
              <td>{money(tot.tax)}</td>
            </tr>
            <tr className="is-ttc">
              <th>Total TTC</th>
              <td>{money(tot.ttc)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {!locked && draft.id && onDelete ? (
        <div className="fx-quote-foot">
          <Button variant="danger" onClick={onDelete} disabled={busy}>
            Supprimer
          </Button>
        </div>
      ) : null}
    </form>
      {previewUrl && !pdfOpen ? (
        <iframe ref={pdfFrame} className="fx-quote-pdf__off" title={`${title} PDF`} src={previewUrl} />
      ) : null}
      {pdfOpen && previewUrl ? (
        <QuotePdfDrawer src={previewUrl} frameRef={pdfFrame} onPrint={printPdf} onClose={() => setPdfOpen(false)} />
      ) : null}
      {traceOpen && draft.id ? <QuoteTraceDrawer id={draft.id} onClose={() => setTraceOpen(false)} /> : null}
    </div>
  );
}
